/**
 * 성취기준 선택기 — 학교급/과목 매핑, 검색 정규화, 삽입 텍스트.
 * 비교용 정규화는 CONTRACT §2, 과목 사전 선택은 §4·§5-1(계약 v1.1)을 따른다.
 */

import {
  DEFAULT_CURRICULUM,
  SPECIAL_CURRICULUM,
  SUPPORTED_CURRICULA,
  normalizeCurriculumId,
} from '../../config/curricula';

export { DEFAULT_CURRICULUM, SPECIAL_CURRICULUM, SUPPORTED_CURRICULA };

export const FORM_SCHOOL_LEVEL_MAP = {
  초등: 'elementary',
  중등: 'middle',
  고등: 'high',
};

export const SCHOOL_LEVEL_LABEL = {
  elementary: '초등학교',
  high: '고등학교',
  middle: '중학교',
  vocational: '전문교과',
};

/** 선택기 상단 세그먼트(초등/중등/고등) 순서와 폼 라벨. 폼 `schoolLevel` 상태와 같은 값을 쓴다. */
export const SCHOOL_LEVEL_SEGMENTS = [
  { formLabel: '초등', id: 'elementary' },
  { formLabel: '중등', id: 'middle' },
  { formLabel: '고등', id: 'high' },
];

/**
 * 폼 과목 칩(`subjectList`) → catalog 라벨 매칭용 별칭.
 * catalog에 `form_subject_map`이 없을 때만 쓰는 폴백이다.
 */
const FORM_SUBJECT_ALIASES = {
  과학: ['과학'],
  국어: ['국어'],
  기가정보: ['기가정보', '기술·가정', '기술가정', '정보'],
  도덕: ['도덕'],
  미술: ['미술'],
  사회: ['사회'],
  수학: ['수학'],
  영어: ['영어'],
  역사: ['역사'],
  음악: ['음악'],
  제2외국어: ['제2외국어', '독일어', '프랑스어', '스페인어', '중국어', '일본어', '러시아어', '아랍어', '베트남어'],
  지리: ['지리'],
  체육: ['체육'],
  한문: ['한문'],
};

const DOT_CHARS = /[\u22C5\u00B7\uFF65\u30FB\u2219\u2022\u318D\u119E]/g;
const DASH_CHARS = /[\u2010-\u2015\u2212]/g;
const FULLWIDTH_ROMAN = {
  Ⅰ: 'I',
  Ⅱ: 'II',
  Ⅲ: 'III',
  Ⅳ: 'IV',
  Ⅴ: 'V',
  Ⅵ: 'VI',
  Ⅶ: 'VII',
  Ⅷ: 'VIII',
  Ⅸ: 'IX',
  Ⅹ: 'X',
};

export const mapFormSchoolLevel = (formLevel) => FORM_SCHOOL_LEVEL_MAP[formLevel] || null;

export const schoolLevelLabel = (levelId) => SCHOOL_LEVEL_LABEL[levelId] || '';

/** 학년도: 3월 시작. 1~2월은 전년도. */
export const schoolYearFromDate = (date = new Date()) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  return month <= 2 ? year - 1 : year;
};

const parseGradeNumber = (grade) => {
  if (grade === null || grade === undefined || grade === '') return null;
  if (typeof grade === 'number' && Number.isFinite(grade)) return grade;
  const matched = String(grade).match(/([1-3])\s*$/);
  return matched ? Number(matched[1]) : null;
};

const resolveLevelId = (schoolLevel) => {
  if (!schoolLevel) return null;
  return mapFormSchoolLevel(schoolLevel) || (
    ['elementary', 'middle', 'high'].includes(schoolLevel) ? schoolLevel : null
  );
};

/** 중·고에서만 2015 전환을 보여준다. 초등(2015 없음)은 숨긴다. */
export const showCurriculumSwitch = ({ schoolLevel } = {}) => {
  const levelId = resolveLevelId(schoolLevel);
  return levelId === 'middle' || levelId === 'high';
};

/**
 * 기본 교육과정.
 * 2026학년도 중3·고3, 2027학년도 고3 → 2015. 학년 정보가 없으면 2022.
 */
export const defaultCurriculumFor = ({ schoolLevel, grade, schoolYear } = {}) => {
  const levelId = resolveLevelId(schoolLevel);
  const gradeNumber = parseGradeNumber(grade);
  if (!levelId || gradeNumber == null) return DEFAULT_CURRICULUM;
  const year = schoolYear == null ? schoolYearFromDate() : Number(schoolYear);
  if (!Number.isFinite(year)) return DEFAULT_CURRICULUM;
  const isMiddle3 = levelId === 'middle' && gradeNumber === 3;
  const isHigh3 = levelId === 'high' && gradeNumber === 3;
  if (year === 2026 && (isMiddle3 || isHigh3)) return '2015';
  if (year === 2027 && isHigh3) return '2015';
  return DEFAULT_CURRICULUM;
};

/**
 * 학년도별로 2015 개정이 남아 있는 학년(설계 D4). `defaultCurriculumFor`와 같은 표.
 * 반환: { year, levelId, levelShort: '중'|'고', grades2015: [3] } 또는 전환이 없으면 null.
 */
export const curriculumTransitionFor = ({ schoolLevel, schoolYear } = {}) => {
  const levelId = resolveLevelId(schoolLevel);
  if (levelId !== 'middle' && levelId !== 'high') return null;
  const year = schoolYear == null ? schoolYearFromDate() : Number(schoolYear);
  if (!Number.isFinite(year)) return null;
  const grades2015 = (year === 2026 || (year === 2027 && levelId === 'high')) ? [3] : [];
  if (grades2015.length === 0) return null;
  return {
    grades2015,
    levelId,
    levelShort: levelId === 'middle' ? '중' : '고',
    year,
  };
};

const joinGrades = (grades) => grades.join('·');

/**
 * 교육과정 사실 문장 + 변경 버튼 라벨(설계 D4). 교사는 "개정"을 몰라도 자기 학년만 보고 판단한다.
 *   2022 → "2026학년도 중3은 2015 개정 교육과정을 적용받아요" / "2015 개정으로 보기"
 *   2015 → "중1·2는 2022 개정 교육과정을 적용받아요" / "2022 개정으로 보기"
 * 전환이 없는 학교급·학년도(초등, 2028 이후)는 null.
 */
export const curriculumNotice = ({ curriculum, schoolLevel, schoolYear } = {}) => {
  if (curriculum === SPECIAL_CURRICULUM) return null;
  const transition = curriculumTransitionFor({ schoolLevel, schoolYear });
  if (!transition) return null;
  const { grades2015, levelShort, year } = transition;
  if (curriculum === '2015') {
    const others = [1, 2, 3].filter((grade) => !grades2015.includes(grade));
    return {
      sentence: `${levelShort}${joinGrades(others)}는 2022 개정 교육과정을 적용받아요`,
      switchLabel: '2022 개정으로 보기',
      switchTo: '2022',
    };
  }
  return {
    sentence: `${year}학년도 ${levelShort}${joinGrades(grades2015)}은 2015 개정 교육과정을 적용받아요`,
    switchLabel: '2015 개정으로 보기',
    switchTo: '2015',
  };
};

export const recordCurriculum = (record) => normalizeCurriculumId(record?.curriculum);

export const recordIdentity = (record) => `${recordCurriculum(record)}:${record?.key || ''}`;

export const isCurriculum2015 = (record) => recordCurriculum(record) === '2015';
export const isSpecialCurriculum = (record) => recordCurriculum(record) === SPECIAL_CURRICULUM;

/**
 * 고시에 코드가 없어 데이터셋이 위치 표시로 만든 코드인가(2015 전문교과Ⅱ).
 * 서버 `code_kind`가 없던 응답(캐시)에도 같은 판정이 나오도록 교육과정·학교급으로도 본다.
 */
export const isLocatorCode = (record) => {
  if (record?.code_kind === 'locator') return true;
  const level = record?.school_level
    || (String(record?.subject_id || '').startsWith('vocational-') ? 'vocational' : '');
  return recordCurriculum(record) === '2015' && level === 'vocational';
};

/** 입력칸에 넣는 한 줄. 위치 표시 코드는 고시 코드로 옮겨 적히지 않게 원문만 넣는다. */
export const standardLineFor = (record) => {
  const text = String(record?.text || '').trim();
  if (isLocatorCode(record)) return text;
  return `${record?.code || ''} ${text}`.trim();
};

/** 입력칸에 그 성취기준 줄이 아직 남아 있는가(교사가 지웠으면 payload에서 뺀다). */
export const standardStillInText = (text, record) => {
  const value = String(text || '');
  if (isLocatorCode(record)) {
    const body = String(record?.text || '').trim();
    return Boolean(body) && value.includes(body);
  }
  const code = String(record?.code || '').trim();
  return Boolean(code) && value.includes(code);
};

/** CONTRACT §2 비교용 정규화: NFKC → 중점 8종 → `·` → 대시류 → `-` → 공백 제거 → 끝 마침표 제거 */
export const normalizeSearchQuery = (query) => {
  if (query === null || query === undefined) return '';
  let normalized = String(query).normalize('NFKC');
  normalized = normalized.replace(DOT_CHARS, '·');
  normalized = normalized.replace(DASH_CHARS, '-');
  normalized = normalized.replace(/\s+/g, '');
  return normalized.replace(/[.。．]+$/g, '');
};

/** CONTRACT §2 `code_norm`: 대괄호 제거, 공백 제거, 대시류 → `-`, 전각 로마숫자 → 라틴, NFC */
export const normalizeCode = (code) => {
  if (!code) return '';
  let normalized = String(code).normalize('NFC').replace(/[[\]]/g, '');
  normalized = normalized.replace(DASH_CHARS, '-');
  normalized = normalized.replace(/[ⅠⅡⅢⅣⅤⅥⅦⅧⅨⅩ]/g, (char) => FULLWIDTH_ROMAN[char] || char);
  return normalized.replace(/\s+/g, '');
};

const subjectIdBelongsToLevel = (subjectId, schoolLevelId) => (
  typeof subjectId === 'string' && subjectId.startsWith(`${schoolLevelId}-`)
);

/** `form_subject_map[폼 과목 칩]` 중 해당 학교급(`elementary|middle|high|vocational`)의 subject_id만 고른다. */
export const resolveSubjectIds = (formSubjectLabel, formSubjectMap = {}, schoolLevelId) => {
  const candidates = Array.isArray(formSubjectMap?.[formSubjectLabel])
    ? formSubjectMap[formSubjectLabel]
    : [];
  if (!schoolLevelId) return [...candidates];
  return candidates.filter((subjectId) => subjectIdBelongsToLevel(subjectId, schoolLevelId));
};

export const pickPreferredSubjectId = (subjectIds, { gradeBand } = {}) => {
  if (!Array.isArray(subjectIds) || subjectIds.length === 0) return null;
  if (gradeBand) {
    const bandMatch = subjectIds.find((subjectId) => subjectId.endsWith(`-${gradeBand}`));
    if (bandMatch) return bandMatch;
  }
  return subjectIds[0];
};

export const getSchoolLevelEntry = (catalog, levelId) => (
  catalog?.school_levels?.find((level) => level.id === levelId) || null
);

/**
 * 학교급의 교과군 → 과목 트리. 초등은 학년군으로 과목을 걸러 과목이 없는 교과군은 뺀다.
 * 반환: [{ id, label, subjects: [{ ...subject, groupId, groupLabel }] }]
 */
export const buildSubjectGroups = (catalog, levelId, { gradeBand } = {}) => {
  const level = getSchoolLevelEntry(catalog, levelId);
  if (!level) return [];
  return (level.subject_groups || [])
    .map((group) => ({
      id: group.id,
      label: group.label,
      subjects: (group.subjects || [])
        .filter((subject) => !gradeBand || !subject.grade_band || subject.grade_band === gradeBand)
        .map((subject) => ({ ...subject, groupId: group.id, groupLabel: group.label })),
    }))
    .filter((group) => group.subjects.length > 0);
};

const collectLevelSubjects = (level) => {
  const subjects = [];
  for (const group of level?.subject_groups || []) {
    for (const subject of group.subjects || []) {
      subjects.push({ ...subject, groupId: group.id, groupLabel: group.label });
    }
  }
  return subjects;
};

const preferGradeBand = (subjects, gradeBand) => {
  if (subjects.length === 0) return null;
  if (gradeBand) {
    const bandMatch = subjects.find((subject) => subject.grade_band === gradeBand);
    if (bandMatch) return bandMatch;
  }
  return subjects[0];
};

/**
 * 폼 과목 칩 → catalog 과목 사전 선택.
 * 1) catalog.form_subject_map(계약 v1.1) → 2) catalog.aliases(정규화 표기 → subject_id)
 * → 3) 라벨 매칭 폴백(과목 라벨 정확 일치 > 과목 라벨 포함 > 교과군 라벨 포함).
 * 반환값은 catalog의 subject 항목(+ groupId/groupLabel) 또는 null.
 */
export const resolveSubjectFromCatalog = (catalog, {
  formLabel,
  gradeBand,
  schoolLevelId,
} = {}) => {
  const level = getSchoolLevelEntry(catalog, schoolLevelId);
  if (!level || !formLabel) return null;
  const subjects = collectLevelSubjects(level);
  const subjectsById = new Map(subjects.map((subject) => [subject.id, subject]));

  const mappedIds = resolveSubjectIds(formLabel, catalog?.form_subject_map, schoolLevelId)
    .filter((subjectId) => subjectsById.has(subjectId));
  const mappedId = pickPreferredSubjectId(mappedIds, { gradeBand });
  if (mappedId) return subjectsById.get(mappedId);

  const aliases = catalog?.aliases && typeof catalog.aliases === 'object' ? catalog.aliases : {};
  const aliasId = aliases[formLabel] || aliases[normalizeSearchQuery(formLabel)];
  if (aliasId && subjectsById.has(aliasId)) return subjectsById.get(aliasId);

  const needles = (FORM_SUBJECT_ALIASES[formLabel] || [formLabel])
    .map(normalizeSearchQuery)
    .filter(Boolean);
  const scored = subjects.map((subject) => {
    const label = normalizeSearchQuery(subject.label);
    const groupLabel = normalizeSearchQuery(subject.groupLabel);
    let score = 0;
    if (needles.includes(label)) score = 3;
    else if (needles.some((needle) => label.includes(needle))) score = 2;
    else if (needles.some((needle) => groupLabel === needle || groupLabel.includes(needle))) score = 1;
    return { score, subject };
  }).filter((entry) => entry.score > 0);
  if (scored.length === 0) return null;
  const best = Math.max(...scored.map((entry) => entry.score));
  return preferGradeBand(
    scored.filter((entry) => entry.score === best).map((entry) => entry.subject),
    gradeBand,
  );
};

/** 영역 구분이 없는 과목(계약 v1.2)의 `domain: null`을 탭 id로 쓸 때의 대체값 */
export const NULL_DOMAIN_ID = '__none__';
export const NULL_DOMAIN_LABEL = '기타';

const hasDomainLabel = (domain) => typeof domain === 'string' && domain.trim() !== '';

/** `domain`(null·빈 문자열 포함) → 탭 id */
export const domainIdOf = (domain) => (hasDomainLabel(domain) ? domain : NULL_DOMAIN_ID);

/**
 * standards 응답의 `domains[]` → 영역 탭 목록. 그룹이 하나뿐이고 domain이 null이면 탭 없이 평면 목록(`flat: true`).
 * 여러 그룹에 null이 섞이면(발생하면 안 되지만) 그 탭은 "기타"로 보인다.
 */
export const buildDomainTabs = (payload) => {
  const groups = Array.isArray(payload?.domains) ? payload.domains : [];
  const tabs = groups.map((group) => ({
    id: domainIdOf(group.domain),
    label: hasDomainLabel(group.domain) ? group.domain : NULL_DOMAIN_LABEL,
  }));
  const flat = tabs.length === 1 && tabs[0].id === NULL_DOMAIN_ID;
  return { flat, tabs: flat ? [] : tabs };
};

export const flattenSubjectStandards = (payload) => {
  if (!payload?.domains) return [];
  return payload.domains.flatMap((domain) => (
    (domain.standards || []).map((standard) => ({
      ...standard,
      // 영역이 없는 과목은 null을 그대로 둔다(빈 문자열로 바꾸지 않음)
      domain: hasDomainLabel(standard.domain) ? standard.domain : (hasDomainLabel(domain.domain) ? domain.domain : null),
      domain_order: standard.domain_order ?? domain.domain_order,
    }))
  ));
};

const editionSchoolYear = (effectiveFrom) => {
  const matched = String(effectiveFrom || '').match(/^(\d{4})-(\d{2})/);
  if (!matched) return null;
  return Number(matched[2]) <= 2 ? Number(matched[1]) - 1 : Number(matched[1]);
};

const isDatedEdition = (record) => Boolean(
  record?.effective_from && record?.edition && record.edition !== 'current',
);

/**
 * 시행일이 다른 판(통합교과 2022-33 / 2026-1). 오래된 판부터. 판이 하나뿐이면 빈 배열.
 * `schoolYear`는 그 판을 처음 쓰는 학년도(3월 1일 시행 → 그해).
 */
export const editionsOf = (records) => {
  const byEdition = new Map();
  (records || []).forEach((record) => {
    if (!isDatedEdition(record) || byEdition.has(record.edition)) return;
    byEdition.set(record.edition, {
      edition: record.edition,
      effectiveFrom: record.effective_from,
      notice: record.source?.notice || '',
      schoolYear: editionSchoolYear(record.effective_from),
    });
  });
  const editions = [...byEdition.values()].sort((a, b) => String(a.effectiveFrom).localeCompare(String(b.effectiveFrom)));
  return editions.length > 1 ? editions : [];
};

/** 그 학년도에 시행 중인 판(가장 늦게 시행된 것). 아직 시행된 판이 없으면 가장 이른 판. */
export const activeEditionFor = (editions, schoolYear) => {
  if (!editions?.length) return null;
  const year = Number(schoolYear);
  const effective = editions.filter((entry) => entry.schoolYear != null && entry.schoolYear <= year);
  return (effective[effective.length - 1] || editions[0]).edition;
};

/**
 * 과목마다 판이 여럿이면 한 판만 남긴다(같은 코드가 두 번 보이지 않게).
 * `overrides`는 과목 id → 교사가 고른 판. 판 구분이 없는 레코드는 그대로 둔다.
 */
export const filterRecordsByEdition = (records, schoolYear, overrides = {}) => {
  const list = records || [];
  const bySubject = new Map();
  list.forEach((record) => {
    if (!isDatedEdition(record)) return;
    const id = record.subject_id || '';
    if (!bySubject.has(id)) bySubject.set(id, []);
    bySubject.get(id).push(record);
  });
  const keep = new Map();
  bySubject.forEach((subjectRecords, id) => {
    const editions = editionsOf(subjectRecords);
    if (editions.length === 0) return;
    const override = overrides[id];
    keep.set(id, editions.some((entry) => entry.edition === override)
      ? override
      : activeEditionFor(editions, schoolYear));
  });
  if (keep.size === 0) return list;
  return list.filter((record) => (
    !isDatedEdition(record)
    || !keep.has(record.subject_id || '')
    || keep.get(record.subject_id || '') === record.edition
  ));
};

export const matchesSearch = (record, query) => {
  const normalizedQuery = normalizeSearchQuery(query);
  if (!normalizedQuery) return true;
  const haystack = normalizeSearchQuery(`${record.code || ''} ${record.text || ''} ${record.domain || ''}`);
  const code = normalizeCode(record.code);
  const queryCode = normalizeCode(normalizedQuery);
  return haystack.includes(normalizedQuery) || Boolean(queryCode && code.includes(queryCode));
};

const SCHOOL_LEVEL_ORDER = { elementary: 0, middle: 1, high: 2, vocational: 3 };

/**
 * 전체 검색 결과 정렬(계약 v1.4, 사용자 피드백 9/10 14:38). 서버가 같은 규칙으로 정렬해 주지만,
 * 구버전 서버·캐시 응답에도 같은 화면이 나오게 클라이언트에서 한 번 더 고정한다.
 * 코드 정확 일치 → 현재 과목 → 현재 학교급 → 2022 먼저 → 학교급 순서 → 서버가 준 순서.
 */
export const rankSearchResults = (results, {
  curriculum,
  query,
  schoolLevelId,
  subjectId,
} = {}) => {
  const queryCode = normalizeCode(normalizeSearchQuery(query));
  const preferredCurriculum = SUPPORTED_CURRICULA.includes(curriculum) ? curriculum : null;
  const scored = (results || []).map((record, index) => {
    const code = record.code_norm || normalizeCode(record.code);
    return {
      index,
      record,
      score: [
        queryCode && code === queryCode ? 0 : 1,
        // 선택기가 보고 있는 교육과정(사실 문장의 현재 값)을 먼저 — 같은 코드가 2022·2015에 다 있을 때 정합성
        preferredCurriculum && recordCurriculum(record) === preferredCurriculum ? 0 : 1,
        subjectId && record.subject_id === subjectId ? 0 : 1,
        schoolLevelId && record.school_level === schoolLevelId ? 0 : 1,
        isCurriculum2015(record) ? 1 : 0,
        SCHOOL_LEVEL_ORDER[record.school_level] ?? 9,
      ],
    };
  });
  scored.sort((a, b) => {
    for (let i = 0; i < a.score.length; i += 1) {
      if (a.score[i] !== b.score[i]) return a.score[i] - b.score[i];
    }
    return a.index - b.index;
  });
  return scored.map((item) => item.record);
};

export const buildInsertText = (records) => (
  (records || [])
    .map(standardLineFor)
    .filter(Boolean)
    .join('\n')
);

/** textarea에 `[코드] 원문` 줄을 덧붙인다. 이미 같은 코드로 시작하는 줄이 있으면 건너뛴다. */
export const appendStandardLines = (existingText, records) => {
  const existing = existingText || '';
  const seenCodes = new Set(
    existing
      .split('\n')
      .map((line) => {
        const match = line.match(/^\s*(\[[^\]]+\])/);
        return match ? normalizeCode(match[1]) : '';
      })
      .filter(Boolean),
  );
  const nextLines = [];
  (records || []).forEach((record) => {
    if (isLocatorCode(record)) {
      const line = standardLineFor(record);
      if (!line || existing.includes(line) || nextLines.includes(line)) return;
      nextLines.push(line);
      return;
    }
    const code = normalizeCode(record.code);
    if (!code || seenCodes.has(code)) return;
    seenCodes.add(code);
    nextLines.push(`${record.code} ${record.text}`.trim());
  });
  if (nextLines.length === 0) return existing;
  if (!existing.trim()) return nextLines.join('\n');
  return `${existing.replace(/\s+$/g, '')}\n${nextLines.join('\n')}`;
};

/** 같은 교육과정·과목·코드인데 key가 다르면(통합교과 판 둘) 같은 성취기준의 다른 판이다. */
const isOtherEditionOf = (record, target) => (
  recordIdentity(record) !== recordIdentity(target)
  && recordCurriculum(record) === recordCurriculum(target)
  && Boolean(record?.subject_id)
  && record.subject_id === target?.subject_id
  && normalizeCode(record.code) === normalizeCode(target?.code)
);

export const capSelection = (currentRecords, nextRecord, maxSelect = 10) => {
  const records = [...(currentRecords || [])];
  if (!nextRecord?.key) return { limited: false, records };
  const nextId = recordIdentity(nextRecord);
  const existingIndex = records.findIndex((record) => recordIdentity(record) === nextId);
  if (existingIndex >= 0) {
    records.splice(existingIndex, 1);
    return { limited: false, records };
  }
  // 다른 판을 담으면 앞 판을 바꾼다(같은 코드의 다른 문장이 함께 들어가지 않게)
  const kept = records.filter((record) => !isOtherEditionOf(record, nextRecord));
  if (kept.length >= maxSelect) {
    return { limited: true, records };
  }
  return { limited: false, records: [...kept, nextRecord] };
};

export const toRecordSummary = (record, fallbackCurriculum) => ({
  code: record.code,
  code_kind: record.code_kind || undefined,
  curriculum: recordCurriculum({
    curriculum: record.curriculum || fallbackCurriculum,
  }),
  domain: record.domain,
  grade_band: record.grade_band,
  key: record.key,
  school_level: record.school_level,
  subject: record.subject,
  subject_id: record.subject_id,
  text: record.text,
});

export const mergeSelectedStandards = (existing, incoming) => {
  const merged = new Map();
  (existing || []).forEach((record) => {
    if (record?.key) merged.set(recordIdentity(record), toRecordSummary(record));
  });
  (incoming || []).forEach((record) => {
    if (record?.key) merged.set(recordIdentity(record), toRecordSummary(record));
  });
  return [...merged.values()];
};

export const toAchievementStandardRefs = (selectedStandards) => {
  if (!Array.isArray(selectedStandards) || selectedStandards.length === 0) return null;
  return selectedStandards.map((record) => ({
    code: record.code || null,
    curriculum: recordCurriculum(record),
    key: record.key,
    text: record.text || null,
  }));
};

export const attachAchievementStandardsToPayload = (payload, selectedStandards) => {
  const refs = toAchievementStandardRefs(selectedStandards);
  if (!refs) return payload;
  return {
    ...payload,
    achievement_standards: refs,
  };
};

export const formatSourceTooltip = (source) => {
  if (!source) return '';
  const parts = [];
  if (source.notice) parts.push(source.notice);
  if (source.annex) parts.push(`별책${source.annex}`);
  if (source.printed_page) parts.push(`${source.printed_page}쪽`);
  return parts.join(' · ');
};

export const isVerified = (record) => record?.verification?.status === 'verified';
